import React, { useEffect, useRef } from 'react';
import Icon from '../AppIcon';

/**
 * Individual toast item. Auto-dismisses after `duration` ms.
 */
const ToastItem = ({ id, message, type = 'info', onDismiss, duration = 5000 }) => {
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => onDismiss(id), duration);
    return () => clearTimeout(timerRef.current);
  }, [id, duration, onDismiss]);

  const styles = {
    info: {
      container: 'bg-card border-border text-foreground',
      icon: 'Info',
      iconClass: 'text-primary',
    },
    success: {
      container: 'bg-card border-green-500/50 text-foreground',
      icon: 'CheckCircle2',
      iconClass: 'text-green-500',
    },
    warning: {
      container: 'bg-card border-amber-500/50 text-foreground',
      icon: 'AlertCircle',
      iconClass: 'text-amber-500',
    },
    error: {
      container: 'bg-card border-destructive/50 text-foreground',
      icon: 'XCircle',
      iconClass: 'text-destructive',
    },
  };

  const s = styles[type] ?? styles.info;

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg min-w-[280px] max-w-sm animate-slide-in ${s.container}`}
      role="alert"
      aria-live="polite"
    >
      <Icon name={s.icon} size={18} className={`mt-0.5 shrink-0 ${s.iconClass}`} />
      <p className="text-sm flex-1 leading-snug">{message}</p>
      <button
        onClick={() => onDismiss(id)}
        className="text-muted-foreground hover:text-foreground transition-colors shrink-0 mt-0.5"
        aria-label="Dismiss notification"
      >
        <Icon name="X" size={16} />
      </button>
    </div>
  );
};

/**
 * Toast container — renders toasts in a fixed portal at the bottom-right.
 * Limits to 5 visible toasts (oldest dropped first).
 */
const ToastContainer = ({ toasts, onDismiss }) => {
  const visible = toasts.slice(-5);

  if (visible.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none">
      {visible.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem {...toast} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
